import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

import BasicDetails from "./BasicDetails";
import T3Details from "./T3Details";
// IdeaSelection omitted from flow — component kept in ./IdeaSelection.jsx for easy revert
import PostPreview from "./PostPreview";
import { AnimatePresence, motion } from "framer-motion";
import StepProgressBar from "../../step_progress_bar/StepProgressBar";
import Loader from "../../loader/Loader";
import { useAuthModal, ONBOARDING_AUTH_MODAL, GUEST_POSTS_LIMIT_AUTH_MODAL, GUEST_SAVE_CAMPAIGN_AUTH_MODAL } from "../../../context/AuthModalContext";
import {
    fetchPostsCombined,
    fetchPostsGuest,
    publishCampaign,
    loadCampaign,
    resetCampaignBuilder,
    seedCampaignBuilderFromPreferences,
} from "../../../store/campaignBuilderSlice";
import { isCampaignQuotaExhausted } from "../../../utils/normalizePlan";
import { isBusinessProfileEmpty } from "../../../utils/isBusinessProfileEmpty";
import AlertBanner from "../../AlertBanner";
import { ANALYTICS_CTAS, ANALYTICS_FEATURES, ANALYTICS_ENTRY_POINTS, ANALYTICS_GENERATION_MODES } from "../../../analytics/analytics.constants";
import PageHeader from "../../shared/PageHeader";
import {
    isTruthyQueryParam,
    isValidWizardRef,
    normalizeAppPath,
} from "../../../utils/campaignWizardNavigation";
import { APP_ROUTES, buildCampaignPath } from "../../../constants/appRoutes";
import { trackCtaClicked, trackPostGenerated } from "../../../analytics/analytics";
import { useFeatureStarted } from "../../../analytics/analytics.hooks";

const STEP_KEYS = ["basicInfo", "generatePosts", "saveCampaign"];

const stepObject = Object.fromEntries(STEP_KEYS.map((key) => [key, true]));

/** Maps persisted backend wizardStep (0–3) to visible UI step index (0–2). */
function resolveStepIndex(wizardStep) {
    if (wizardStep === "published") return STEP_KEYS.length - 1;
    if (typeof wizardStep === "number") {
        if (wizardStep >= 3) return 2;
        if (wizardStep === 2) return 1;
        if (wizardStep >= 0 && wizardStep < STEP_KEYS.length) return wizardStep;
    }
    return 0;
}

/** Draft resume: posts already generated — allow finishing publish without plan/quota/profile blocks. */
function canResumeDraftCampaign({ routeCampaignId, campaignStatus, postsCount }) {
    if (!routeCampaignId || postsCount <= 0) return false;
    // Guest generate sets posts + id but may not hydrate status until refresh/loadCampaign
    return campaignStatus === "draft" || campaignStatus == null;
}

function getStepReadOnly(stepKey, { isGuest, wizardBlocked, postsGenerated }) {
    if (wizardBlocked) return true;
    if (isGuest && stepKey !== "basicInfo" && stepKey !== "generatePosts") return true;
    if (postsGenerated && (stepKey === "basicInfo" || stepKey === "generatePosts")) return true;
    return false;
}

function getNextButtonConfig(stepKey, { advanceBusy, postsLoading }) {
    if (stepKey === "generatePosts") {
        const busy = advanceBusy || postsLoading;
        return {
            label: busy ? "Generating posts…" : "Generate posts",
            icon: "bi-stars",
            showSpinner: busy,
        };
    }

    return {
        label: "Next",
        icon: "bi-arrow-right",
        iconAfter: true,
        showSpinner: false,
    };
}

const Wizard = ({ curStep = 0 }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { campaignId: routeCampaignId } = useParams();
    const [searchParams] = useSearchParams();
    const wizardRef = normalizeAppPath(searchParams.get("ref"));
    const wizardBackRoute = isValidWizardRef(wizardRef) ? wizardRef : null;
    const fastForwardParam = isTruthyQueryParam(searchParams.get("fast_forward"));
    const wizardQuery = (() => {
        const params = new URLSearchParams();
        if (wizardBackRoute) params.set("ref", wizardBackRoute);
        if (fastForwardParam) params.set("fast_forward", "true");
        const query = params.toString();
        return query ? `?${query}` : "";
    })();
    const fastForwardOnEntry = useRef(!routeCampaignId && fastForwardParam);
    const [fastForward, setFastForward] = useState(fastForwardOnEntry.current);
    const user = useSelector((state) => state.auth.user);
    const userId = user?.uid;
    const isGuest = !user;
    const { openAuthModal, guestId, hasUsedGuestPostsToday, recordGuestPostUsage } = useAuthModal();

    useFeatureStarted(ANALYTICS_FEATURES.CAMPAIGN_BUILDER, true, {
        entry_point: ANALYTICS_ENTRY_POINTS.WIZARD,
        is_guest: isGuest,
    });

    const {
        campaignId,
        campaignStatus,
        postsLoading,
        publishLoading,
        posts,
        ideas,
        publishedCampaign,
    } = useSelector((state) => state.campaignBuilder);
    const { data: planData, loading: planLoading, loaded: planLoaded } = useSelector(
        (state) => state.plan
    );
    const { data: profileData, loading: profileLoading } = useSelector((state) => state.profile);

    const profileEmpty = !profileLoading && isBusinessProfileEmpty(profileData);
    const campaignExhausted = isCampaignQuotaExhausted(planData);
    const showNoPlanAlert = planLoaded && !planLoading && (!planData || !planData.hasPlans);
    const postsGenerated = posts.length > 0;
    const resumeDraftCampaign = canResumeDraftCampaign({
        routeCampaignId,
        campaignStatus,
        postsCount: posts.length,
    });

    const wizardBlockReason = isGuest
        ? null
        : profileEmpty && !resumeDraftCampaign
          ? "profile"
          : showNoPlanAlert && !resumeDraftCampaign
            ? "noPlan"
            : campaignExhausted && !routeCampaignId
              ? "exhausted"
              : null;
    const wizardBlocked = Boolean(wizardBlockReason);

    const [currentStep, setCurrentStep] = useState(() =>
        fastForwardOnEntry.current ? 1 : curStep
    );
    const [direction, setDirection] = useState("next");
    const [basicStepValid, setBasicStepValid] = useState(false);
    const [followUpStepValid, setFollowUpStepValid] = useState(false);
    const [stepError, setStepError] = useState(null);
    const [prefsReady, setPrefsReady] = useState(!routeCampaignId);
    const [campaignHydrated, setCampaignHydrated] = useState(!routeCampaignId);
    const [advanceBusy, setAdvanceBusy] = useState(false);

    useLayoutEffect(() => {
        if (!routeCampaignId) {
            dispatch(
                seedCampaignBuilderFromPreferences({
                    userId,
                    fastForward: fastForwardOnEntry.current,
                })
            );
            setPrefsReady(true);
        }
    }, [dispatch, routeCampaignId, userId]);

    useEffect(() => {
        if (!routeCampaignId) {
            setCampaignHydrated(true);
            return;
        }

        let cancelled = false;
        const hasLocalDraft =
            campaignId === routeCampaignId && (ideas.length > 0 || posts.length > 0);

        // Always re-fetch on auth change so the backend can attach the logged-in user to the campaign.
        if (!hasLocalDraft) {
            setCampaignHydrated(false);
        }

        dispatch(loadCampaign(routeCampaignId))
            .unwrap()
            .then((campaign) => {
                if (cancelled) return;

                if (campaign.status === "published") {
                    navigate(buildCampaignPath(routeCampaignId), { replace: true });
                    return;
                }

                if (!hasLocalDraft) {
                    setCurrentStep(resolveStepIndex(campaign.wizardStep));
                }
                setPrefsReady(true);
                setCampaignHydrated(true);
            })
            .catch(() => {
                if (!cancelled) {
                    setCampaignHydrated(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [dispatch, navigate, routeCampaignId, userId, campaignId, ideas.length, posts.length]);

    useEffect(() => {
        if (!publishedCampaign) return;

        const id = publishedCampaign.id ?? campaignId ?? routeCampaignId;
        const timer = setTimeout(() => {
            dispatch(resetCampaignBuilder());
            navigate(buildCampaignPath(id));
        }, 1500);

        return () => clearTimeout(timer);
    }, [publishedCampaign, campaignId, routeCampaignId, dispatch, navigate]);

    useEffect(() => {
        if (!wizardBlocked) return;
        setBasicStepValid(false);
        setFollowUpStepValid(false);
    }, [wizardBlocked]);

    const renderBlockAlert = () => {
        if (wizardBlockReason === "profile") {
            return (
                <AlertBanner
                    variant="emphasis"
                    iconClass="building"
                    title="Complete your business profile"
                    action={{
                        label: "Set up profile",
                        cta: ANALYTICS_CTAS.SETUP_PROFILE,
                        onClick: () => openAuthModal(ONBOARDING_AUTH_MODAL),
                    }}
                    className="mb-0 shadow-lg"
                >
                    Your business profile unlocks the post builder. Add your brand name and a few
                    details so we can generate on-brand campaigns and posts for you.
                </AlertBanner>
            );
        }

        if (wizardBlockReason === "noPlan") {
            return (
                <AlertBanner
                    variant="emphasis"
                    iconClass="gem"
                    title="No active plan"
                    action={{ label: "View plans", cta: ANALYTICS_CTAS.VIEW_PLANS, to: "/plans" }}
                    className="mb-0 shadow-lg"
                >
                    Choose a plan to unlock campaigns and posts for your account.
                </AlertBanner>
            );
        }

        if (wizardBlockReason === "exhausted") {
            const { limit = 0, used = 0 } = planData.campaign ?? {};

            return (
                <AlertBanner
                    variant="emphasis"
                    iconClass="megaphone"
                    title="You've reached your plan limit"
                    action={{ label: "View plans", cta: ANALYTICS_CTAS.UPGRADE_PLAN, to: "/plans" }}
                    className="mb-0 shadow-lg"
                >
                    Your {planData.activePlan?.name ?? "current"} plan includes {limit}{" "}
                    {limit === 1 ? "campaign" : "campaigns"} per billing period — and you&apos;ve used all{" "}
                    {used}. Upgrade your plan to create more campaigns, or manage the ones you&apos;ve
                    already built from your campaigns list.
                </AlertBanner>
            );
        }

        return null;
    };

    const renderStep = (stepKey) => {
        const readOnly = getStepReadOnly(stepKey, {
            isGuest,
            wizardBlocked,
            postsGenerated,
        });
        switch (stepKey) {
            case "basicInfo":
                return <BasicDetails readOnly={readOnly} onValidityChange={setBasicStepValid} />;
            case "generatePosts":
                return (
                    <T3Details
                        readOnly={readOnly}
                        onValidityChange={setFollowUpStepValid}
                        fastForward={fastForward}
                        onFastForwardHandled={() => setFastForward(false)}
                    />
                );
            case "saveCampaign":
                return <PostPreview readOnly={readOnly} />;
            default:
                return null;
        }
    };

    const stepValidity = {
        basicInfo: postsGenerated || basicStepValid,
        generatePosts: postsGenerated || followUpStepValid,
        saveCampaign: posts.length > 0 && !postsLoading,
    };

    const isFooterBusy = publishLoading || advanceBusy;
    const guestOnEarlyStep = isGuest && currentStep <= 1;
    const canGoNext =
        !wizardBlocked &&
        (stepValidity[STEP_KEYS[currentStep]] ?? true) &&
        !isFooterBusy &&
        (!isGuest || guestOnEarlyStep);

    const handleNext = () => {
        if (wizardBlocked || !canGoNext) return;

        const stepKey = STEP_KEYS[currentStep];

        if (isGuest) {
            if (stepKey === "basicInfo") {
                setDirection("next");
                setStepError(null);
                setCurrentStep(1);
                return;
            }

            if (stepKey === "generatePosts") {
                if (hasUsedGuestPostsToday) {
                    trackCtaClicked(ANALYTICS_CTAS.AUTH_LOGIN);
                    openAuthModal(GUEST_POSTS_LIMIT_AUTH_MODAL);
                    return;
                }
                void advanceFromStep(stepKey);
                return;
            }

            return;
        }

        if (stepKey === "generatePosts") {
            if (posts.length > 0) {
                setDirection("next");
                setStepError(null);
                setCurrentStep(2);
                return;
            }
            void advanceFromStep(stepKey);
            return;
        }

        void advanceFromStep(stepKey);
    };

    const advanceFromStep = async (stepKey) => {
        if (wizardBlocked) return;
        if (isGuest && stepKey !== "generatePosts") return;

        setDirection("next");
        setStepError(null);
        setAdvanceBusy(true);

        try {
            if (stepKey === "basicInfo") {
                setCurrentStep((prev) => prev + 1);
                return;
            }

            if (stepKey === "generatePosts") {
                setCurrentStep(2);
                trackCtaClicked(ANALYTICS_CTAS.GENERATE_POSTS, { is_guest: isGuest });

                if (isGuest) {
                    if (hasUsedGuestPostsToday) {
                        trackCtaClicked(ANALYTICS_CTAS.AUTH_LOGIN);
                        openAuthModal(GUEST_POSTS_LIMIT_AUTH_MODAL);
                        setCurrentStep(1);
                        return;
                    }

                    try {
                        const result = await dispatch(fetchPostsGuest({ guestId })).unwrap();
                        if (result.guestId) {
                            recordGuestPostUsage(result.guestId);
                        }

                        const newCampaignId = result.campaignId;
                        trackPostGenerated({
                            status: "success",
                            is_guest: true,
                            generation_mode: ANALYTICS_GENERATION_MODES.GUEST,
                            post_count: Array.isArray(result.posts) ? result.posts.length : posts.length,
                            campaign_id: newCampaignId,
                        });

                        if (newCampaignId) {
                            navigate(`${APP_ROUTES.CAMPAIGN_WIZARD}/${newCampaignId}${wizardQuery}`, {
                                replace: true,
                            });
                        }
                    } catch (guestErr) {
                        const forbiddenGuestId = guestErr?.guest_id ?? guestErr?.guestId ?? null;
                        trackPostGenerated({
                            status: "failed",
                            is_guest: true,
                            generation_mode: ANALYTICS_GENERATION_MODES.GUEST,
                            error_code: guestErr?.status ?? guestErr?.code ?? "guest_generate_failed",
                        });

                        if (guestErr?.status === 403 || forbiddenGuestId) {
                            if (forbiddenGuestId) {
                                recordGuestPostUsage(forbiddenGuestId);
                            }
                            trackCtaClicked(ANALYTICS_CTAS.AUTH_LOGIN);
                            openAuthModal({
                                ...GUEST_POSTS_LIMIT_AUTH_MODAL,
                                description:
                                    typeof guestErr?.detail === "string"
                                        ? guestErr.detail
                                        : GUEST_POSTS_LIMIT_AUTH_MODAL.description,
                            });
                            setCurrentStep(1);
                            return;
                        }
                        throw guestErr;
                    }
                    return;
                }

                try {
                    const result = await dispatch(fetchPostsCombined()).unwrap();
                    const newCampaignId = result.campaignId;
                    trackPostGenerated({
                        status: "success",
                        is_guest: false,
                        generation_mode: ANALYTICS_GENERATION_MODES.COMBINED,
                        post_count: Array.isArray(result.posts) ? result.posts.length : posts.length,
                        campaign_id: newCampaignId,
                    });

                    if (newCampaignId) {
                        navigate(`${APP_ROUTES.CAMPAIGN_WIZARD}/${newCampaignId}${wizardQuery}`, {
                            replace: true,
                        });
                    }
                } catch (combinedErr) {
                    trackPostGenerated({
                        status: "failed",
                        is_guest: false,
                        generation_mode: ANALYTICS_GENERATION_MODES.COMBINED,
                        error_code:
                            combinedErr?.status ?? combinedErr?.code ?? "combined_generate_failed",
                    });
                    throw combinedErr;
                }
                return;
            }
        } catch (err) {
            const message =
                typeof err === "string"
                    ? err
                    : err?.message || "Something went wrong. Please try again.";
            setStepError(message);
            console.error(`Error in step "${stepKey}":`, err);
        } finally {
            setAdvanceBusy(false);
        }
    };

    const handleGuestSaveCampaign = () => {
        if (posts.length === 0) return;

        trackCtaClicked(ANALYTICS_CTAS.AUTH_LOGIN);
        openAuthModal(GUEST_SAVE_CAMPAIGN_AUTH_MODAL);
    };

    const handlePublishCampaign = async () => {
        if (isGuest || wizardBlocked || posts.length === 0 || publishLoading) return;

        setStepError(null);

        try {
            await dispatch(publishCampaign()).unwrap();
        } catch (err) {
            const message =
                typeof err === "string"
                    ? err
                    : err?.message || "Failed to publish campaign. Please try again.";
            setStepError(message);
            console.error("Error publishing campaign:", err);
        }
    };

    const handlePrev = () => {
        if (wizardBlocked) return;
        if (isGuest && currentStep > 1) return;
        setDirection("prev");
        setStepError(null);
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleWizardRefBack = () => {
        if (!wizardBackRoute) return;
        navigate(wizardBackRoute);
    };

    const hasCampaignId = Boolean(routeCampaignId || campaignId);
    const showStepBack =
        currentStep > 0 &&
        (hasCampaignId ||
            (!(postsGenerated && currentStep <= 1) && !(postsGenerated && currentStep >= 2)));
    const showRefBack = currentStep === 0 && Boolean(wizardBackRoute);
    const showFooterBack = showStepBack || showRefBack;
    const nextButton = getNextButtonConfig(STEP_KEYS[currentStep], {
        advanceBusy,
        postsLoading,
    });

    const showInitialLoader =
        (!isGuest && (profileLoading || !planLoaded || planLoading)) ||
        (routeCampaignId && !campaignHydrated && !postsLoading) ||
        (!routeCampaignId && !prefsReady);

    if (showInitialLoader) {
        return (
            <div className="relative -mx-3 min-h-[calc(100vh-4.75rem)] md:min-h-screen md:-mx-10">
                <Loader isLoading />
            </div>
        );
    }

    return (
        <div className="relative -mx-3 min-h-[calc(100vh-4.75rem)] md:min-h-screen md:-mx-10">
            <Loader isLoading={publishLoading} />
            <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden />
            <div
                className="relative z-[2] px-3 pb-28 md:px-10 md:pb-28"
            >
                <div className="relative mt-0 py-2 md:py-4">
                    <div className="fixed inset-x-0 top-14 z-30 flex h-[4.5rem] items-center border-b border-slate-200/80 bg-white/95 px-8 [&_.step-wrapper]:!mb-0 md:hidden">
                        <div className="w-full">
                            <StepProgressBar currentStep={currentStep} steps={stepObject} />
                        </div>
                    </div>
                    <div className="h-[4.5rem] md:hidden" aria-hidden />
                    <PageHeader
                        title="Post Builder"
                        description="Clear and thoughtful inputs help generate beautiful, engaging posts that connect with your audience."
                        className="mb-2"
                    />
                    <div className="relative min-h-[calc(100vh-12rem)]">
                        {wizardBlocked ? (
                            <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/50 px-4 backdrop-blur-sm">
                                <div className="pointer-events-auto w-full max-w-3xl">
                                    {renderBlockAlert()}
                                </div>
                            </div>
                        ) : null}

                        <div
                            className={
                                wizardBlocked ? "pointer-events-none select-none blur-[2px]" : ""
                            }
                        >
                            <div className="hidden border-b border-slate-200/80 bg-white/95 py-2 md:-mx-10 md:block md:px-10 md:pb-2">
                                <StepProgressBar currentStep={currentStep} steps={stepObject} />
                            </div>

                            {stepError ? (
                                <div className="mx-1 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 md:mx-2">
                                    {stepError}
                                </div>
                            ) : null}

                            <div className="row justify-content-center pt-2 md:pt-3">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentStep}
                                    className="w-full"
                                    initial={{ x: direction === "next" ? 300 : -300, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: direction === "next" ? -300 : 300, opacity: 0 }}
                                    transition={{ duration: 0.4 }}
                                    style={{ minHeight: "72vh" }}
                                >
                                    {renderStep(STEP_KEYS[currentStep])}
                                </motion.div>
                            </AnimatePresence>
                            <div className="fixed inset-x-0 bottom-0 z-20 md:left-[248px]">
                                <div
                                    className="pointer-events-none h-8 bg-gradient-to-t from-white to-transparent md:hidden"
                                    aria-hidden
                                />
                                <div className="border-t border-slate-200/80 bg-white/95 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 shadow-[0_-4px_24px_rgba(15,23,42,0.08)] backdrop-blur-sm md:px-10 md:pt-3">
                                    <div
                                        className={`mx-auto flex max-w-7xl items-center py-2 md:py-3 ${showFooterBack ? "justify-between" : "justify-end"}`}
                                    >
                                        {showFooterBack ? (
                                            <button
                                                className="btn btn-dark-app inline-flex items-center justify-center gap-2 px-3 py-2 text-sm md:px-5 md:py-2.5"
                                                onClick={showStepBack ? handlePrev : handleWizardRefBack}
                                                type="button"
                                                disabled={
                                                    (isGuest && currentStep > 1) ||
                                                    (showStepBack && (publishLoading || wizardBlocked))
                                                }
                                            >
                                                <i className="bi bi-arrow-left leading-none" aria-hidden />
                                                Back
                                            </button>
                                        ) : null}
                                        {!wizardBlocked && currentStep === STEP_KEYS.length - 1 ? (
                                            <button
                                                className="btn btn-dark-app inline-flex items-center justify-center gap-2 px-3 py-2 text-sm md:px-5 md:py-2.5"
                                                type="button"
                                                onClick={isGuest ? handleGuestSaveCampaign : handlePublishCampaign}
                                                disabled={
                                                    posts.length === 0 ||
                                                    (!isGuest && (publishLoading || !!publishedCampaign))
                                                }
                                            >
                                                {!isGuest && publishLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                                                        Saving…
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-rocket-takeoff leading-none" aria-hidden />
                                                        Save Campaign
                                                    </>
                                                )}
                                            </button>
                                        ) : !wizardBlocked && (!isGuest || guestOnEarlyStep) ? (
                                            <button
                                                className="btn btn-dark-app inline-flex items-center justify-center gap-2 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 md:px-5 md:py-2.5"
                                                onClick={handleNext}
                                                type="button"
                                                disabled={!canGoNext}
                                            >
                                                {nextButton.showSpinner ? (
                                                    <span
                                                        className="spinner-border spinner-border-sm"
                                                        role="status"
                                                        aria-hidden
                                                    />
                                                ) : nextButton.icon && !nextButton.iconAfter ? (
                                                    <i className={`bi ${nextButton.icon} leading-none`} aria-hidden />
                                                ) : null}
                                                {nextButton.label}
                                                {nextButton.icon && nextButton.iconAfter ? (
                                                    <i className={`bi ${nextButton.icon} leading-none`} aria-hidden />
                                                ) : null}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Wizard;